import { useEffect, useMemo, useState, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { useTheme, keyframes } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import HorizontalBar from './HorizontalBar';
import MainContentStyled from './MainContentStyled';
// import Customization from '../Customization';
import Loader from 'ui-component/Loader';
import OrganizationSetupModal from 'views/components/OrganizationSetupModal';
import ConsentModal from 'views/components/ConsentModal';
import { useNavTheme } from 'contexts/NavThemeContext';
import { useSelector } from 'store';
import { selectNeedsConsent, selectPolicyConfig, updateProfile } from 'store/slices/auth';
import { useDispatch } from 'react-redux';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| ANIMATED BACKGROUND ||============================== //

const floatAnimation = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(15px, -20px) scale(1.05); }
  66% { transform: translate(-10px, 15px) scale(0.95); }
`;

const floatAnimationReverse = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-20px, 15px) scale(0.95); }
  66% { transform: translate(15px, -15px) scale(1.05); }
`;

const pageEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface AnimatedBackgroundProps {
  mode: 'dark' | 'light';
}

function AnimatedBackground({ mode }: AnimatedBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
      color: string;
      pulsePhase: number;
    }

    const particles: Particle[] = [];
    const numParticles = 40;
    const connectionDistance = 120;

    for (let i = 0; i < numParticles; i++) {
      particles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * 0.15,
        vy: (Math.random() - 0.5) * 0.15,
        size: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.3 + 0.15,
        color: Math.random() > 0.5 ? '#6366f1' : '#818cf8',
        pulsePhase: Math.random() * Math.PI * 2,
      });
    }

    let animationId: number;
    let time = 0;

    const animate = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      time += 0.008;

      // Draw subtle grid
      ctx.strokeStyle = 'rgba(99, 102, 241, 0.012)';
      ctx.lineWidth = 1;
      const gridSize = 80;

      for (let x = 0; x < window.innerWidth; x += gridSize) {
        ctx.beginPath();
        const offset = Math.sin(time + x * 0.001) * 1;
        ctx.moveTo(x, offset);
        ctx.lineTo(x, window.innerHeight + offset);
        ctx.stroke();
      }

      for (let y = 0; y < window.innerHeight; y += gridSize) {
        ctx.beginPath();
        const offset = Math.cos(time + y * 0.001) * 1;
        ctx.moveTo(offset, y);
        ctx.lineTo(window.innerWidth + offset, y);
        ctx.stroke();
      }

      // Update and draw particles
      particles.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.pulsePhase += 0.012;

        if (particle.x < -10) particle.x = window.innerWidth + 10;
        if (particle.x > window.innerWidth + 10) particle.x = -10;
        if (particle.y < -10) particle.y = window.innerHeight + 10;
        if (particle.y > window.innerHeight + 10) particle.y = -10;

        // Draw connections
        for (let j = i + 1; j < particles.length; j++) {
          const other = particles[j];
          const distance = Math.hypot(particle.x - other.x, particle.y - other.y);
          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.08;
            ctx.strokeStyle = `rgba(99, 102, 241, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        }

        // Draw particle with glow
        const pulse = Math.sin(particle.pulsePhase) * 0.2 + 0.8;

        const gradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.size * 4
        );
        gradient.addColorStop(0, particle.color + Math.floor(particle.opacity * pulse * 150).toString(16).padStart(2, '0'));
        gradient.addColorStop(0.5, particle.color + '08');
        gradient.addColorStop(1, particle.color + '00');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 4, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = particle.color + Math.floor(particle.opacity * 180).toString(16).padStart(2, '0');
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * pulse, 0, Math.PI * 2);
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Only render animated background in dark mode
  if (mode === 'light') {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          overflow: 'hidden',
          pointerEvents: 'none',
          background: 'linear-gradient(180deg, #f8f9fc 0%, #ffffff 50%, #f8f9fc 100%)',
          transition: 'background 0.4s ease',
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        pointerEvents: 'none',
        transition: 'background 0.4s ease',
      }}
    >
      {/* Base dark gradient - matches sidebar #08080c */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(180deg,
              #08080c 0%,
              #0a0a10 30%,
              #0c0c14 70%,
              #08080c 100%
            )
          `,
        }}
      />

      {/* Canvas particle animation */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.9,
        }}
      />

      {/* Floating gradient orbs - indigo palette */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 400,
          height: 400,
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%)',
          filter: 'blur(60px)',
          animation: `${floatAnimation} 25s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: '5%',
          width: 350,
          height: 350,
          background: 'radial-gradient(circle, rgba(129, 140, 248, 0.08) 0%, transparent 70%)',
          filter: 'blur(70px)',
          animation: `${floatAnimationReverse} 30s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: '30%',
          width: 300,
          height: 300,
          background: 'radial-gradient(circle, rgba(165, 180, 252, 0.04) 0%, transparent 70%)',
          filter: 'blur(50px)',
          animation: `${floatAnimation} 20s ease-in-out infinite reverse`,
        }}
      />

      {/* Noise texture overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.02,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />
    </Box>
  );
}

// ==============================|| MAIN LAYOUT ||============================== //

export default function MainLayout() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));  // < 900px (mobile - no sidebar)
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));  // < 1200px (tablet - collapsed sidebar)
  const { user } = useAuth();
  const [showOrgSetup, setShowOrgSetup] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const { mode, tokens } = useNavTheme();
  const location = useLocation();
  const [pageKey, setPageKey] = useState(0);
  const dispatch = useDispatch();
  const needsConsent = useSelector(selectNeedsConsent);
  const policyConfig = useSelector(selectPolicyConfig);

  // Trigger animation on route change
  useEffect(() => {
    setPageKey(prev => prev + 1);
  }, [location.pathname]);

  const {
    state: { borderRadius, container, miniDrawer, menuOrientation }
  } = useConfig();
  const { menuMaster, menuMasterLoading } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  // Fetch policyConfig if not loaded yet (e.g., right after login)
  useEffect(() => {
    if (user && policyConfig === null) {
      dispatch(updateProfile() as any);
    }
  }, [user, policyConfig, dispatch]);

  // Check if user needs to accept consent (takes priority over org setup)
  useEffect(() => {
    if (user && needsConsent) {
      setShowConsent(true);
    } else {
      setShowConsent(false);
    }
  }, [user, needsConsent]);

  // Check if user needs to set up organization (only after consent is done AND policyConfig loaded)
  useEffect(() => {
    if (user && !user.organizationId && !needsConsent && policyConfig !== null) {
      setShowOrgSetup(true);
    }
  }, [user, needsConsent, policyConfig]);

  // Responsive drawer behavior on breakpoint change:
  // - Mobile (< md): Close drawer (temporary overlay via hamburger)
  // - Tablet (md to lg): Start collapsed, but hamburger can expand it
  // - Desktop (>= lg): Start expanded, unless miniDrawer config is set
  useEffect(() => {
    if (downMD) {
      handlerDrawerOpen(false);
    } else if (downLG) {
      handlerDrawerOpen(false);
    } else {
      handlerDrawerOpen(!miniDrawer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downMD, downLG]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  // horizontal menu-list bar : drawer
  const menu = useMemo(() => (isHorizontal ? <HorizontalBar /> : <Sidebar />), [isHorizontal]);

  const handleConsentComplete = () => {
    setShowConsent(false);
    // Consent accepted - auth state already updated via Redux
    // Check if org setup is needed now
    if (user && !user.organizationId) {
      setShowOrgSetup(true);
    }
  };

  const handleOrgSetupComplete = () => {
    setShowOrgSetup(false);
    // User context will be updated via localStorage, no need to reload
  };

  if (menuMasterLoading) return <Loader />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Animated Background - renders behind everything */}
      <AnimatedBackground mode={mode} />

      {/* Consent Modal - shown first if user needs to accept TOS/Privacy Policy */}
      {user && showConsent && (
        <ConsentModal
          open={showConsent}
          onComplete={handleConsentComplete}
        />
      )}

      {/* Organization Setup Modal - shown after consent if user has no org */}
      {user && showOrgSetup && !showConsent && (
        <OrganizationSetupModal
          open={showOrgSetup}
          userId={user.id}
          onComplete={handleOrgSetupComplete}
        />
      )}

      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          background: mode === 'dark'
            ? 'linear-gradient(180deg, rgba(8, 8, 12, 0.98) 0%, rgba(8, 8, 12, 0.95) 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${tokens.borderSubtle}`,
          boxShadow: mode === 'dark'
            ? '0 4px 30px rgba(0, 0, 0, 0.3), 0 1px 0 rgba(255, 255, 255, 0.02) inset'
            : '0 2px 12px rgba(15, 23, 42, 0.06)',
          transition: 'all 0.4s ease',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '10%',
            right: '10%',
            height: '1px',
            background: tokens.gradientDivider,
            pointerEvents: 'none',
            transition: 'background 0.4s ease',
          },
        }}
      >
        <Toolbar sx={{ p: isHorizontal ? 1.25 : 2 }}>
          <Header />
        </Toolbar>
      </AppBar>

      {/* menu / drawer */}
      {menu}

      {/* main content */}
      <MainContentStyled {...{ borderRadius, menuOrientation, open: drawerOpen }}>
        <Container
          maxWidth={container ? 'lg' : false}
          sx={{ px: 0, minHeight: 'calc(100vh - 128px)', display: 'flex', flexDirection: 'column' }}
        >
          <Box
            key={pageKey}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              animation: `${pageEnter} 0.3s ease-out`,
            }}
          >
            <Outlet />
          </Box>
          <Footer />
        </Container>
      </MainContentStyled>
      {/* <Customization /> */}
    </Box>
  );
}
